import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { getDarPending, createDarPending, updateDarPending, uploadDarPending, getDarContemplated, getSapEmployeeFetch, getDarCharges, createDarContemplated } from "../../../core/data/redux/darSlice";
import { uploadFile, getPresignedUrl } from "../../../core/data/redux/awsBucketSlice";
import { AppDispatch } from "../../../core/data/redux/store";
import CharacterCounter from "../../../core/common/CharacterCounter";
import moment from "moment";
import Select, { components } from "react-select";

const CustomOption = (props: any) => {
  return (
    <components.Option {...props}>
      <div className="d-flex align-items-center gap-2">
        <input
          type="checkbox"
          className="form-check-input m-0"
          checked={props.isSelected}
          onChange={() => null}
          style={{ cursor: "pointer" }}
        />
        <span className="text-dark" style={{ fontSize: "14px" }}>{props.label}</span>
      </div>
    </components.Option>
  );
};

const DarPending: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const casenoParam = searchParams.get("caseno");
  const isCreate = !casenoParam;
  const todayStr = new Date().toISOString().split("T")[0];

  const [loading, setLoading] = useState(false);
  const [initialFetchLoading, setInitialFetchLoading] = useState(false);
  const [searchCaseNo, setSearchCaseNo] = useState("");
  const [caseFetched, setCaseFetched] = useState(!isCreate);
  const [creationMode, setCreationMode] = useState<"contemplated" | "direct">("contemplated");

  const [formData, setFormData] = useState({
    caseno: "",
    empno: "",
    daname: "",
    dadesignation: "",
    daempno: "",
    dadepartment: "",
    casetype: "",
    casedescription: "",
    approvalfile: "",
    approval_url: "",
    chargesheetdate: "",
    charge_type: "",
    remarks: "",
    chargesheetfile: "",
    chargesheet_url: "",
    da_source: "Internal",
    charges: [] as string[]
  });

  const [chargeOptions, setChargeOptions] = useState<any[]>([]);
  const [loadingCharges, setLoadingCharges] = useState(false);

  useEffect(() => {
    setLoadingCharges(true);
    dispatch(getDarCharges({}))
      .unwrap()
      .then((res: any) => {
        const candidate = res?.data?.data ?? res?.data ?? res;
        setChargeOptions(Array.isArray(candidate) ? candidate : []);
      })
      .catch(() => {
        // Charges Master not configured yet; checkboxes will show an empty state.
      })
      .finally(() => setLoadingCharges(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const [employeeDetails, setEmployeeDetails] = useState<any>({
    empname: "",
    designation: "",
    doj: "",
    basicpay: "",
    annualincdate: "",
    lastpromodate: "",
    payscl: "",
    deptcode: ""
  });

  const [fetchingEmployee, setFetchingEmployee] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docName, setDocName] = useState("");
  const [fetchingDa, setFetchingDa] = useState(false);

  const handleViewFile = async (fileKey: string) => {
    if (!fileKey) return;
    const toastId = toast.loading("Fetching document...");
    try {
      const url = await dispatch(getPresignedUrl(fileKey)).unwrap();
      toast.dismiss(toastId);
      window.open(url, "_blank");
    } catch (error) {
      toast.dismiss(toastId);
      toast.error("Could not fetch document URL.");
    }
  };

  const fetchDaDetails = async (daNumber?: string) => {
    const empNo = daNumber || formData.daempno;
    if (!empNo || empNo.trim().length < 3) {
      toast.warning("Please enter a valid DA Employee No.");
      return;
    }
    setFetchingDa(true);
    try {
      const res = await dispatch(getSapEmployeeFetch({ emp_no: empNo.trim(), role: "DA" })).unwrap();
      if (res?.status === 200 && res?.data) {
        setFormData(prev => ({
          ...prev,
          daname: res.data.empname || "",
          dadesignation: res.data.designation || "",
          dadepartment: res.data.deptname || ""
        }));
      } else {
        const errorMsg = res?.message || res?.data?.message || res?.error || "DA Employee not found.";
        toast.error(errorMsg);
      }
    } catch (err: any) {
      const errorMsg = typeof err === 'string' ? err : (err?.message || err?.error || err?.data?.message || err?.response?.data?.message || "Error fetching DA data.");
      toast.error(errorMsg);
    } finally {
      setFetchingDa(false);
    }
  };

  const fetchEmployeeDetails = async () => {
    if (!formData.empno || formData.empno.length < 3) {
      toast.warning("Please enter a valid Employee No.");
      return;
    }
    setFetchingEmployee(true);
    try {
      const res = await dispatch(getSapEmployeeFetch({ emp_no: formData.empno })).unwrap();
      if (res?.status === 200 && res?.data) {
        setEmployeeDetails({
          empname: res.data.empname || "",
          designation: res.data.designation || "",
          doj: res.data.doj || "",
          basicpay: res.data.basicpay || "",
          annualincdate: res.data.annualincdate || "",
          lastpromodate: res.data.lastpromodate || "",
          payscl: res.data.payscl || "",
          deptcode: res.data.deptname || ""
        });
      } else {
        const errorMsg = res?.message || res?.data?.message || res?.error || "Employee not found.";
        toast.error(errorMsg);
      }
    } catch (err: any) {
      const errorMsg = typeof err === 'string' ? err : (err?.message || err?.error || err?.data?.message || err?.response?.data?.message || "Error fetching employee data.");
      toast.error(errorMsg);
    } finally {
      setFetchingEmployee(false);
    }
  };

  useEffect(() => {
    toast.dismiss();
    if (casenoParam) {
      setInitialFetchLoading(true);
      dispatch(getDarPending({ caseno: casenoParam }))
        .unwrap()
        .then((res: any) => {
          if (res?.status === 200 && res?.data) {
            const data = res.data;
            setFormData(prev => ({
              ...prev,
              caseno: data.caseno || "",
              empno: data.empno || "",
              daname: data.daname || "",
              dadesignation: data.dadesignation || "",
              daempno: data.daempno || "",
              dadepartment: data.dadepartment || data.da_department || "",
              casetype: data.casetype || "",
              casedescription: data.casedescription || "",
              approvalfile: data.approvalfile || "",
              approval_url: data.urls?.approval_url || data.url || "",
              chargesheetdate: data.chargesheetdate ? moment(data.chargesheetdate).format("YYYY-MM-DD") : "",
              charge_type: data.charge_type || data.chargetype || data.type_of_charges || "",
              remarks: data.remarks || "",
              chargesheetfile: data.chargesheetfile || "",
              chargesheet_url: data.urls?.chargesheet_url || "",
              charges: Array.isArray(data.charges)
                ? data.charges.map((c: any) => typeof c === "object" ? Number(c.id) : Number(c))
                : Array.isArray(data.charge_ids)
                  ? data.charge_ids.map((c: any) => Number(c))
                  : []
            }));

            // Fetch subject employee details from SAP
            if (data.empno) {
              dispatch(getSapEmployeeFetch({ emp_no: data.empno }))
                .unwrap()
                .then((empRes: any) => {
                  if (empRes?.status === 200 && empRes?.data) {
                    setEmployeeDetails({
                      empname: empRes.data.empname || "",
                      designation: empRes.data.designation || "",
                      doj: empRes.data.doj || "",
                      basicpay: empRes.data.basicpay || "",
                      annualincdate: empRes.data.annualincdate || "",
                      lastpromodate: empRes.data.lastpromodate || "",
                      payscl: empRes.data.payscl || "",
                      deptcode: empRes.data.deptname || ""
                    });
                  }
                })
                .catch(() => { });
            }

            // Fetch DA details from SAP
            if (data.daempno) {
              dispatch(getSapEmployeeFetch({ emp_no: data.daempno, role: "DA" }))
                .unwrap()
                .then((daRes: any) => {
                  if (daRes?.status === 200 && daRes?.data) {
                    setFormData(prev => ({
                      ...prev,
                      daname: daRes.data.empname || prev.daname,
                      dadesignation: daRes.data.designation || prev.dadesignation,
                      dadepartment: daRes.data.deptname || prev.dadepartment
                    }));
                  }
                })
                .catch(() => { });
            }

            if (data.approvalfile && !data.urls?.approval_url) {
              dispatch(getPresignedUrl(data.approvalfile))
                .unwrap()
                .then((url: any) => setFormData(prev => ({ ...prev, approval_url: url as string })))
                .catch(() => { });
            }

            if (data.chargesheetfile && !data.urls?.chargesheet_url) {
              dispatch(getPresignedUrl(data.chargesheetfile))
                .unwrap()
                .then((url: any) => {
                  setFormData(prev => ({ ...prev, chargesheet_url: url as string }));
                  setDocName(data.chargesheetfile.split("/").pop() || data.chargesheetfile);
                })
                .catch(() => { });
            } else if (data.chargesheetfile) {
              setDocName(data.chargesheetfile.split("/").pop() || data.chargesheetfile);
            }

            if (!data.approvalfile) {
              setCreationMode("direct");
            } else {
              setCreationMode("contemplated");
            }

            setCaseFetched(true);
          } else {
            toast.error(res?.message || "Failed to fetch case details");
            navigate("/dar-cases/pending");
          }
        })
        .catch((err: any) => {
          toast.error(err?.message || "Error fetching case details");
          navigate("/dar-cases/pending");
        })
        .finally(() => {
          setInitialFetchLoading(false);
        });
    }
    return () => {
      toast.dismiss();
    };
  }, [casenoParam, dispatch, navigate]);

  const handleFetchContemplated = async () => {
    if (!searchCaseNo.trim()) {
      toast.warning("Please enter a Case No.");
      return;
    }
    setInitialFetchLoading(true);
    try {
      const res = await dispatch(getDarContemplated({ caseno: searchCaseNo.trim() })).unwrap();
      if (res?.status === 200 && res?.data) {
        const data = res.data;
        setFormData(prev => ({
          ...prev,
          caseno: data.caseno || searchCaseNo.trim(),
          empno: data.empno || "",
          daname: data.daname || "",
          dadesignation: data.dadesignation || "",
          daempno: data.daempno || "",
          dadepartment: data.dadepartment || data.da_department || "",
          casetype: data.casetype || "",
          casedescription: data.casedescription || "",
          approvalfile: data.approvalfile || "",
          approval_url: data.urls?.approval_url || data.url || "",
        }));

        // Fetch subject employee details from SAP
        if (data.empno) {
          dispatch(getSapEmployeeFetch({ emp_no: data.empno }))
            .unwrap()
            .then((empRes: any) => {
              if (empRes?.status === 200 && empRes?.data) {
                setEmployeeDetails({
                  empname: empRes.data.empname || "",
                  designation: empRes.data.designation || "",
                  doj: empRes.data.doj || "",
                  basicpay: empRes.data.basicpay || "",
                  annualincdate: empRes.data.annualincdate || "",
                  lastpromodate: empRes.data.lastpromodate || "",
                  payscl: empRes.data.payscl || "",
                  deptcode: empRes.data.deptname || ""
                });
              }
            })
            .catch(() => { });
        }

        // Fetch DA details from SAP
        if (data.daempno) {
          dispatch(getSapEmployeeFetch({ emp_no: data.daempno, role: "DA" }))
            .unwrap()
            .then((daRes: any) => {
              if (daRes?.status === 200 && daRes?.data) {
                setFormData(prev => ({
                  ...prev,
                  daname: daRes.data.empname || "",
                  dadesignation: daRes.data.designation || "",
                  dadepartment: daRes.data.deptname || ""
                }));
              }
            })
            .catch(() => { });
        }

        setCaseFetched(true);
      } else {
        toast.error(res?.message || "Could not find contemplated case.");
      }
    } catch (err: any) {
      toast.error(err?.message || "Error fetching case.");
    } finally {
      setInitialFetchLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!isCreate && !formData.caseno) {
      toast.error("Case No is required to upload document.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB.");
      if (e.target) e.target.value = "";
      return;
    }

    const validTypes = ["application/pdf"];
    if (!validTypes.includes(file.type)) {
      toast.error("Invalid file format. Only PDF files are allowed.");
      if (e.target) e.target.value = "";
      return;
    }

    setUploadingDoc(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = (reader.result as string).split(",")[1];
      try {
        if (!isCreate) {
          // For Update: Use the dedicated pending upload API (it uploads to AWS and attaches)
          const result = await dispatch(
            uploadDarPending({ case_no: formData.caseno, file: base64, mime_type: file.type })
          );

          if (uploadDarPending.fulfilled.match(result)) {
            const s3Key =
              result.payload?.key ||
              result.payload?.data?.key ||
              result.payload?.url ||
              result.payload?.data?.url ||
              "";

            if (s3Key) {
              setFormData(prev => ({ ...prev, chargesheetfile: s3Key }));
              setDocName(file.name);
              toast.success("Document uploaded and attached successfully!");
            } else {
              toast.success("Document uploaded, but failed to retrieve key.");
            }
          } else {
            toast.error("Failed to upload document for pending case.");
          }
        } else {
          // For Create: Use the standard AWS upload API and hold the URL for the creation payload
          const awsResult = await dispatch(
            uploadFile({ filestring: base64, mime_type: file.type, module: "dar" })
          );

          if (uploadFile.fulfilled.match(awsResult)) {
            const s3Key =
              awsResult.payload?.key ||
              awsResult.payload?.data?.key ||
              awsResult.payload?.url ||
              awsResult.payload?.data?.url ||
              "";

            if (s3Key) {
              setFormData(prev => ({ ...prev, chargesheetfile: s3Key }));
              setDocName(file.name);
              toast.success("Document uploaded successfully. Don't forget to save changes!");
            } else {
              toast.error("Failed to retrieve document path from server.");
            }
          } else {
            toast.error("Failed to upload document to AWS.");
          }
        }
      } catch (error) {
        toast.error("Error uploading document.");
      } finally {
        setUploadingDoc(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === "da_source") {
      setFormData(prev => ({
        ...prev,
        da_source: value,
        daempno: "",
        daname: "",
        dadesignation: ""
      }));
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleRemoveFile = () => {
    setFormData(prev => ({ ...prev, chargesheetfile: "" }));
    setDocName("");
    const fileInput = document.getElementById("chargeSheetDoc") as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleSubmit = async () => {
    if (isCreate) {
      if (creationMode === "contemplated" && !formData.caseno) {
        toast.error("Invalid case number. Please fetch a contemplated case first.");
        return;
      }
    } else {
      if (!formData.caseno) {
        toast.error("Invalid case number.");
        return;
      }
    }
    setLoading(true);
    try {
      if (isCreate) {
        let payload;
        if (creationMode === "direct") {
          if (!formData.empno) {
            toast.error("Employee No is required.");
            setLoading(false);
            return;
          }
          if (!formData.daempno) {
            toast.error("DA Employee No is required.");
            setLoading(false);
            return;
          }
          if (!formData.daname) {
            toast.error("DA Name is required.");
            setLoading(false);
            return;
          }
          if (!formData.dadesignation) {
            toast.error("DA Designation is required.");
            setLoading(false);
            return;
          }
          if (!formData.dadepartment) {
            toast.error("DA Department is required.");
            setLoading(false);
            return;
          }
          if (!formData.casetype) {
            toast.error("Type of Case is required.");
            setLoading(false);
            return;
          }
          if (!formData.casedescription || !formData.casedescription.trim()) {
            toast.error("Brief Description of Allegations is required.");
            setLoading(false);
            return;
          }
          if (!formData.charge_type) {
            toast.error("Type of Charges is required.");
            setLoading(false);
            return;
          }
          if (!formData.chargesheetdate) {
            toast.error("Charge sheet date is required.");
            setLoading(false);
            return;
          }

          // 1. Create contemplated case first to save employee/DA details and casetype/casedescription
          const contPayload = {
            emp_no: formData.empno,
            da_source: formData.da_source,
            daname: formData.daname,
            dadesignation: formData.dadesignation,
            daempno: formData.daempno || "",
            casetype: formData.casetype,
            casedescription: formData.casedescription,
            approval_file: formData.chargesheetfile || docName || ""
          };
          const contResponse = await dispatch(createDarContemplated(contPayload)).unwrap();
          const caseNo = contResponse?.data?.caseno || contResponse?.data?.data?.caseno;
          if (!caseNo) {
            toast.error(contResponse?.message || "Failed to initiate base contemplated case.");
            setLoading(false);
            return;
          }

          // 2. Create pending case with that case number
          payload = {
            case_no: caseNo,
            charge_sheet_date: formData.chargesheetdate,
            charge_type: formData.charge_type,
            remarks: formData.remarks,
            charge_sheet_file: formData.chargesheetfile || docName || "",
            charge_ids: formData.charges.map(Number)
          };
        } else {
          payload = {
            case_no: formData.caseno,
            charge_sheet_date: formData.chargesheetdate,
            charge_type: formData.charge_type,
            remarks: formData.remarks,
            charge_sheet_file: formData.chargesheetfile || docName || "",
            charge_ids: formData.charges.map(Number)
          };
        }
        const response = await dispatch(createDarPending(payload)).unwrap();
        const status = response?.status || response?.data?.status;
        const message = response?.message || response?.data?.message;

        if (status === 200 || status === 201) {
          toast.success(message || "Case successfully created!");
          navigate("/dar-cases/pending");
        } else {
          toast.error(message || "Failed to create pending case.");
        }
      } else {
        const payload = {
          case_no: formData.caseno,
          charge_sheet_date: formData.chargesheetdate,
          charge_type: formData.charge_type,
          remarks: formData.remarks,
          charge_ids: formData.charges.map(Number)
        };
        const response = await dispatch(updateDarPending(payload)).unwrap();
        const status = response?.status || response?.data?.status;
        const message = response?.message || response?.data?.message;

        if (status === 200 || status === 201) {
          toast.success(message || "Pending Case updated successfully!");
          navigate("/dar-cases/pending");
        } else {
          toast.error(message || "Failed to update case.");
        }
      }
    } catch (error: any) {
      toast.error(typeof error === 'string' ? error : (error?.message || "Error saving pending case."));
    } finally {
      setLoading(false);
    }
  };

  const appDocName = formData.approvalfile?.split('/').pop() || "Approval Document";
  const disableModeSelect = !!casenoParam || caseFetched || (creationMode === "direct" && !!employeeDetails.empname);

  return (
    <div className="page-wrapper">
      <div className="content">

        {/* PAGE HEADER */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto">
            <h3 className="page-title mb-1">
              {isCreate ? "Create Pending Case" : "Edit Pending Case"}
            </h3>
            <div className="text-muted small">
              {isCreate ? "Transition a contemplated case to pending" : `Editing Case: ${formData.caseno}`}
            </div>
          </div>
          <div className="d-flex gap-2">
            <Link to="/dar-cases/pending" className="btn btn-outline-secondary">
              Back to Overview
            </Link>
          </div>
        </div>

        {initialFetchLoading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "40vh" }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-4 md:p-6">

              {isCreate && !caseFetched && (
                <div className="card bg-light rounded border p-4 mb-4 shadow-none">
                  <h5 className="mb-3 text-dark fw-bold">Select Case Entry Mode</h5>
                  <div className="d-flex gap-4">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="creation_mode"
                        id="mode_contemplated"
                        value="contemplated"
                        checked={creationMode === "contemplated"}
                        disabled={disableModeSelect}
                        onChange={() => {
                          setCreationMode("contemplated");
                          setCaseFetched(false);
                          setSearchCaseNo("");
                          setFormData({
                            caseno: "",
                            empno: "",
                            daname: "",
                            dadesignation: "",
                            daempno: "",
                            dadepartment: "",
                            casetype: "",
                            casedescription: "",
                            approvalfile: "",
                            approval_url: "",
                            chargesheetdate: "",
                            charge_type: "",
                            remarks: "",
                            chargesheetfile: "",
                            chargesheet_url: "",
                            da_source: "Internal",
                            charges: []
                          });
                        }}
                      />
                      <label className="form-check-label fw-semibold" htmlFor="mode_contemplated">
                        Fetch from Contemplated Case
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="creation_mode"
                        id="mode_direct"
                        value="direct"
                        checked={creationMode === "direct"}
                        disabled={disableModeSelect}
                        onChange={() => {
                          setCreationMode("direct");
                          setCaseFetched(true);
                          setFormData({
                            caseno: "",
                            empno: "",
                            daname: "",
                            dadesignation: "",
                            daempno: "",
                            dadepartment: "",
                            casetype: "",
                            casedescription: "",
                            approvalfile: "",
                            approval_url: "",
                            chargesheetdate: "",
                            charge_type: "",
                            remarks: "",
                            chargesheetfile: "",
                            chargesheet_url: "",
                            da_source: "Internal",
                            charges: []
                          });
                        }}
                      />
                      <label className="form-check-label fw-semibold" htmlFor="mode_direct">
                        Direct Entry (without Contemplated)
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {isCreate && creationMode === "contemplated" && caseFetched && (
                <div className="mb-4">
                  <span className="fw-semibold text-muted">Case No: </span>
                  <span className="fw-bold text-dark">{formData.caseno}</span>
                </div>
              )}

              {isCreate && creationMode === "contemplated" && !caseFetched && (
                <div className="mb-5 p-4 bg-light rounded border shadow-none">
                  <h5 className="mb-3">Fetch Contemplated Case</h5>
                  <div className="d-flex gap-3 align-items-end">
                    <div style={{ flex: 1, maxWidth: "400px" }}>
                      <label className="form-label">Case Number</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. DAR-2026-HR-006"
                        value={searchCaseNo}
                        onChange={(e) => setSearchCaseNo(e.target.value)}
                      />
                    </div>
                    <button className="btn btn-primary" onClick={handleFetchContemplated}>
                      <i className="ti ti-search me-2"></i> Fetch Details
                    </button>
                  </div>
                </div>
              )}

              {caseFetched && (
                <>
                  {creationMode === "direct" && isCreate ? (
                    <>
                      {/* SECTION 1: Subject Employee Details */}
                      <h5 className="mb-3 text-primary fw-bold">Section 1: Subject Employee Details</h5>
                      <div className="mb-4">
                        <div className="row align-items-end">
                          <div className="col-md-5">
                            <label className="form-label">Employee No <span className="text-danger">*</span></label>
                            <div className="input-group">
                              <input
                                type="text"
                                className="form-control"
                                name="empno"
                                value={formData.empno}
                                onChange={handleInputChange}
                                placeholder="Enter Employee No"
                              />
                              <button className="btn btn-primary" type="button" onClick={fetchEmployeeDetails} disabled={fetchingEmployee}>
                                {fetchingEmployee ? <i className="ti ti-loader fa-spin"></i> : "Fetch Data"}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {employeeDetails.empname && (
                        <div className="row g-3 mt-3 pt-3 border-top mb-4">
                          <div className="col-md-4">
                            <label className="form-label text-muted small mb-1">Employee Name</label>
                            <input type="text" className="form-control bg-light" value={employeeDetails.empname} readOnly />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label text-muted small mb-1">Designation</label>
                            <input type="text" className="form-control bg-light" value={employeeDetails.designation} readOnly />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label text-muted small mb-1">Department</label>
                            <input type="text" className="form-control bg-light" value={employeeDetails.deptcode} readOnly />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label text-muted small mb-1">Date of Joining</label>
                            <input type="text" className="form-control bg-light" value={employeeDetails.doj} readOnly />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label text-muted small mb-1">Basic Pay</label>
                            <input type="text" className="form-control bg-light" value={employeeDetails.basicpay} readOnly />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label text-muted small mb-1">Pay Scale</label>
                            <input type="text" className="form-control bg-light" value={employeeDetails.payscl} readOnly />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label text-muted small mb-1">Annual Increment</label>
                            <input type="text" className="form-control bg-light" value={employeeDetails.annualincdate} readOnly />
                          </div>
                        </div>
                      )}

                      {/* SECTION 2: Disciplinary Authority (DA) */}
                      <h5 className="mb-3 mt-4 text-primary fw-bold">Section 2: Disciplinary Authority (DA)</h5>
                      <div className="row g-3 mb-4">
                        <div className="col-md-3">
                          <label className="form-label">DA Employee No <span className="text-danger">*</span></label>
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control"
                              name="daempno"
                              value={formData.daempno}
                              onChange={handleInputChange}
                              onBlur={() => {
                                if (formData.daempno && formData.daempno.trim().length >= 3) {
                                  fetchDaDetails();
                                }
                              }}
                              placeholder="e.g. 10234"
                            />
                            <button
                              className="btn btn-primary"
                              type="button"
                              onClick={() => fetchDaDetails()}
                              disabled={fetchingDa}
                            >
                              {fetchingDa ? <i className="ti ti-loader fa-spin"></i> : "Fetch"}
                            </button>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">DA Name <span className="text-danger">*</span></label>
                          <input
                            type="text"
                            className="form-control bg-light"
                            name="daname"
                            value={formData.daname}
                            placeholder="Fetched Name"
                            readOnly
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">DA Designation <span className="text-danger">*</span></label>
                          <input
                            type="text"
                            className="form-control bg-light"
                            name="dadesignation"
                            value={formData.dadesignation}
                            placeholder="Fetched Designation"
                            readOnly
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Department <span className="text-danger">*</span></label>
                          <input
                            type="text"
                            className="form-control bg-light"
                            name="dadepartment"
                            value={formData.dadepartment}
                            placeholder="Fetched Department"
                            readOnly
                          />
                        </div>
                      </div>

                      {/* SECTION 3: CASE DETAILS */}
                      <h5 className="mb-3 mt-4 text-primary fw-bold">Section 3: Case Description</h5>
                      <div className="row g-3 mb-4">
                        <div className="col-md-4">
                          <label className="form-label">Type of Case <span className="text-danger">*</span></label>
                          <select
                            className="form-select"
                            name="casetype"
                            value={formData.casetype}
                            onChange={handleInputChange}
                          >
                            <option value="">Select Case Type</option>
                            <option value="Administrative">Administrative</option>
                            <option value="Vigilance">Vigilance</option>
                          </select>
                        </div>
                        <div className="col-md-12">
                          <label className="form-label">Brief Description of Allegations <span className="text-danger">*</span></label>
                          <textarea
                            className="form-control"
                            rows={4}
                            name="casedescription"
                            value={formData.casedescription}
                            onChange={handleInputChange}
                            placeholder="Provide a brief, factual description of the incident or allegations..."
                            maxLength={1000}
                          />
                          <CharacterCounter value={formData.casedescription} maxLength={1000} />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* SECTION 1 */}
                      <h5 className="mb-3 text-muted">Section 1: Employee Selection (Inherited)</h5>
                      {employeeDetails.empname ? (
                        <div className="row g-3 mb-4">
                          <div className="col-md-3">
                            <label className="form-label text-muted small mb-1">Employee No</label>
                            <input
                              type="text"
                              className="form-control bg-light"
                              value={formData.empno}
                              disabled
                            />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label text-muted small mb-1">Employee Name</label>
                            <input type="text" className="form-control bg-light" value={employeeDetails.empname} readOnly />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label text-muted small mb-1">Designation</label>
                            <input type="text" className="form-control bg-light" value={employeeDetails.designation} readOnly />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label text-muted small mb-1">Department</label>
                            <input type="text" className="form-control bg-light" value={employeeDetails.deptcode} readOnly />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label text-muted small mb-1">Date of Joining</label>
                            <input type="text" className="form-control bg-light" value={employeeDetails.doj} readOnly />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label text-muted small mb-1">Basic Pay</label>
                            <input type="text" className="form-control bg-light" value={employeeDetails.basicpay} readOnly />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label text-muted small mb-1">Pay Scale</label>
                            <input type="text" className="form-control bg-light" value={employeeDetails.payscl} readOnly />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label text-muted small mb-1">Annual Increment</label>
                            <input type="text" className="form-control bg-light" value={employeeDetails.annualincdate} readOnly />
                          </div>
                        </div>
                      ) : (
                        <div className="row g-3 mb-4">
                          <div className="col-md-3">
                            <label className="form-label text-muted small mb-1">Employee No</label>
                            <input
                              type="text"
                              className="form-control bg-light"
                              value={formData.empno}
                              disabled
                            />
                          </div>
                        </div>
                      )}

                      {/* SECTION 2 */}
                      <h5 className="mb-3 mt-4 text-muted">Section 2: Disciplinary Authority (Inherited)</h5>
                      <div className="row g-3 mb-4">
                        <div className="col-md-3">
                          <label className="form-label">DA Employee No</label>
                          <input
                            className="form-control bg-light"
                            value={formData.daempno}
                            disabled
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">DA Name</label>
                          <input
                            className="form-control bg-light"
                            value={formData.daname}
                            disabled
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">DA Designation</label>
                          <input
                            className="form-control bg-light"
                            value={formData.dadesignation}
                            disabled
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Department</label>
                          <input
                            className="form-control bg-light"
                            value={formData.dadepartment}
                            disabled
                          />
                        </div>
                      </div>

                      {/* SECTION 3 */}
                      <h5 className="mb-3 mt-4 text-muted">Section 3: Case Details (Inherited)</h5>
                      <div className="mb-3">
                        <label className="form-label">Case Type</label>
                        <input
                          className="form-control bg-light"
                          value={formData.casetype}
                          disabled
                        />
                      </div>
                      <div className="mb-4">
                        <label className="form-label">Brief Description</label>
                        <textarea
                          className="form-control bg-light"
                          rows={3}
                          value={formData.casedescription}
                          disabled
                        />
                      </div>

                      {/* SECTION 4 */}
                      {creationMode !== "direct" && (
                        <>
                          <h5 className="mb-3 mt-4 text-muted">Section 4: Contemplated Document (Inherited)</h5>
                          <div className="mb-4">
                            {formData.approval_url ? (
                              <div className="d-flex align-items-center gap-3">
                                <a
                                  href={formData.approval_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="btn btn-sm btn-outline-primary d-flex align-items-center gap-2"
                                >
                                  <i className="ti ti-eye"></i> View Document
                                </a>
                              </div>
                            ) : (
                              <div className="text-muted">No document attached.</div>
                            )}
                          </div>
                        </>
                      )}
                    </>
                  )}

                  <div className="border-top my-4"></div>

                  {/* SECTION 5 */}
                  <h5 className="mb-3 text-primary fw-bold">Section {creationMode === "direct" ? "4" : "5"}: Charge Sheet Details</h5>
                  <div className="row g-3 mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Type of Charges <span className="text-danger">*</span></label>
                      <select
                        className="form-select"
                        name="charge_type"
                        value={formData.charge_type}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Type of Charges...</option>
                        <option value="Major Charge">Major Charge</option>
                        <option value="Minor Charge">Minor Charge</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Date of Charge Sheet Issuance <span className="text-danger">*</span></label>
                      <input
                        type="date"
                        className="form-control"
                        name="chargesheetdate"
                        value={formData.chargesheetdate}
                        onChange={handleInputChange}
                        max={todayStr}
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="form-label d-block">Charges</label>
                    {loadingCharges ? (
                      <div className="text-muted small"><span className="spinner-border spinner-border-sm me-2"></span>Loading charges...</div>
                    ) : chargeOptions.length > 0 ? (
                      <Select
                        isMulti
                        isSearchable
                        name="charges"
                        options={chargeOptions.map((opt: any) => ({
                          value: typeof opt === "object" ? Number(opt.id) : opt,
                          label: typeof opt === "object" ? (opt.charge_description ?? opt.name) : opt
                        }))}
                        value={chargeOptions
                          .filter((opt: any) => {
                            const val = typeof opt === "object" ? Number(opt.id) : opt;
                            return formData.charges.map(Number).includes(val);
                          })
                          .map((opt: any) => ({
                            value: typeof opt === "object" ? Number(opt.id) : opt,
                            label: typeof opt === "object" ? (opt.charge_description ?? opt.name) : opt
                          }))}
                        onChange={(selected: any) => {
                          const values = selected ? selected.map((s: any) => Number(s.value)) : [];
                          setFormData(prev => ({ ...prev, charges: values }));
                        }}
                        components={{ Option: CustomOption }}
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        placeholder="Select Charges..."
                      />
                    ) : (
                      <div className="text-muted small fst-italic">No charges configured yet.</div>
                    )}
                  </div>
                  <div className="mb-4">
                    <label className="form-label">Remarks <span className="text-danger">*</span></label>
                    <textarea
                      className="form-control"
                      rows={4}
                      name="remarks"
                      value={formData.remarks}
                      onChange={handleInputChange}
                      placeholder="Enter remarks..."
                      maxLength={1000}
                    />
                    <CharacterCounter value={formData.remarks} maxLength={1000} />
                  </div>

                  {/* SECTION 6 */}
                  <h5 className="mb-3 mt-4 text-primary fw-bold">Section {creationMode === "direct" ? "5" : "6"}: Documents</h5>
                  <div className="mb-4">
                    <label className="form-label text-dark fw-medium">
                      Charge Sheet Document <span className="text-danger">*</span>
                    </label>
                    <div className="d-flex align-items-center gap-3">
                      <input
                        type="file"
                        id="chargeSheetDoc"
                        className="d-none"
                        accept=".pdf"
                        onChange={handleFileUpload}
                        disabled={uploadingDoc}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-primary d-flex align-items-center gap-2"
                        onClick={() => document.getElementById("chargeSheetDoc")?.click()}
                        disabled={uploadingDoc}
                      >
                        <i className="ti ti-upload"></i> {uploadingDoc ? "Uploading..." : "Upload Charge Sheet"}
                      </button>
                      {docName && !uploadingDoc && (
                        <div className="bg-light border border-success-subtle rounded px-3 d-flex align-items-center justify-content-between shadow-sm" style={{ height: "38px", minWidth: "300px", maxWidth: "450px" }}>
                          <div
                            className="d-flex align-items-center gap-2 text-dark overflow-hidden me-2"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleViewFile(formData.chargesheetfile)}
                            title="Click to view file"
                          >
                            <i className="ti ti-file-check text-success fs-5"></i>
                            <div className="text-truncate" style={{ maxWidth: '200px' }}>
                              <span className="fw-medium small text-primary text-decoration-underline" title={docName}>{docName}</span>
                            </div>
                          </div>
                          <div className="d-flex align-items-center gap-1">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center"
                              style={{ width: "28px", height: "28px", padding: 0 }}
                              title="View File"
                              onClick={(e) => { e.preventDefault(); handleViewFile(formData.chargesheetfile); }}
                            >
                              <i className="ti ti-eye" style={{ fontSize: "1rem" }}></i>
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger d-flex align-items-center justify-content-center"
                              style={{ width: "28px", height: "28px", padding: 0 }}
                              title="Remove File"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRemoveFile(); }}
                            >
                              <i className="ti ti-x" style={{ fontSize: "1rem" }}></i>
                            </button>
                          </div>
                        </div>
                      )}
                      {formData.chargesheet_url && !docName && !uploadingDoc && (
                        <div className="d-flex align-items-center gap-3 ms-2">
                          <a href={formData.chargesheet_url} target="_blank" rel="noreferrer" className="text-info text-decoration-none">
                            <i className="ti ti-eye me-1"></i> View Current File
                          </a>
                        </div>
                      )}
                    </div>
                    <div className="text-muted small mt-2">
                      Only PDF files under 5MB are acceptable.
                    </div>
                  </div>

                  {/* ACTIONS */}
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      className="btn btn-primary"
                      onClick={handleSubmit}
                      disabled={loading}
                    >
                      {loading ? "Saving..." : (isCreate ? "Create Pending Case" : "Update Pending Case")}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default DarPending;

